import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit2, FiTrash2, FiCheck, FiX, FiBell, FiPower } = FiIcons;

const ManageAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentAnnouncement, setCurrentAnnouncement] = useState({ text: '', is_active: 1 });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getAnnouncements();
      setAnnouncements(response.data || []);
    } catch (err) {
      console.error('Error fetching announcements:', err);
      setError('Announcements konnten nicht geladen werden.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeactivateAll = async () => {
    // Find all active announcements
    const activeAnnouncements = announcements.filter(a => a.is_active === 1);
    
    // Deactivate them sequentially
    for (const ann of activeAnnouncements) {
      try {
        await apiService.updateAnnouncement(ann.id, { ...ann, is_active: 0 });
      } catch (err) {
        console.error(`Failed to deactivate announcement ${ann.id}`, err);
      }
    }
  };

  const onDeactivateAllClick = async () => {
    const hasActive = announcements.some(a => a.is_active === 1);
    if (!hasActive) return; // Nothing to deactivate

    if (!window.confirm('Möchten Sie wirklich alle aktiven Announcements deaktivieren?')) return;
    
    setIsLoading(true);
    try {
      await handleDeactivateAll();
      await fetchAnnouncements();
    } catch (err) {
      console.error('Error deactivating all:', err);
      setError('Deaktivierung fehlgeschlagen.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      // If we are creating a new one or setting an existing one to active, first deactivate others
      if (currentAnnouncement.is_active === 1) {
        await handleDeactivateAll();
      }

      if (isEditing) {
        await apiService.updateAnnouncement(currentAnnouncement.id, currentAnnouncement);
      } else {
        await apiService.createAnnouncement(currentAnnouncement);
      }

      setAnnouncements([]); // Clear list temporarily
      await fetchAnnouncements(); // Reload all
      setIsEditing(false);
      setCurrentAnnouncement({ text: '', is_active: 1 });
    } catch (err) {
      console.error('Error saving announcement:', err);
      setError('Speichern fehlgeschlagen.');
    }
  };

  const handleEdit = (announcement) => {
    setCurrentAnnouncement(announcement);
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Möchten Sie dieses Announcement wirklich löschen?')) {
      try {
        await apiService.deleteAnnouncement(id);
        setAnnouncements(announcements.filter(a => a.id !== id));
      } catch (err) {
        console.error('Error deleting announcement:', err);
        setError('Löschen fehlgeschlagen.');
      }
    }
  };

  const handleActivate = async (announcement) => {
    if (announcement.is_active === 1) return; // Already active

    try {
      setIsLoading(true);
      await handleDeactivateAll();
      
      await apiService.updateAnnouncement(announcement.id, { ...announcement, is_active: 1 });
      
      await fetchAnnouncements();
    } catch (err) {
      console.error('Error activating announcement:', err);
      setError('Aktivierung fehlgeschlagen.');
      setIsLoading(false);
    }
  };

  // Check if there are any active announcements to enable/disable the button
  const hasActiveAnnouncements = announcements.some(a => a.is_active === 1);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
          <SafeIcon icon={FiBell} className="mr-2" />
          Announcements verwalten
        </h2>
        <button
          onClick={onDeactivateAllClick}
          disabled={!hasActiveAnnouncements || isLoading}
          className={`inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md 
            ${!hasActiveAnnouncements || isLoading 
              ? 'text-gray-400 bg-gray-100 cursor-not-allowed' 
              : 'text-gray-700 bg-white hover:bg-gray-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
            }`}
        >
          <SafeIcon icon={FiPower} className={`mr-2 h-4 w-4 ${!hasActiveAnnouncements ? 'text-gray-400' : 'text-gray-500'}`} />
          Alle deaktivieren
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <SafeIcon icon={FiX} className="h-5 w-5 text-red-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {isEditing ? 'Announcement bearbeiten' : 'Neues Announcement erstellen'}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="text" className="block text-sm font-medium text-gray-700">
              Text
            </label>
            <textarea
              id="text"
              required
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-primary focus:ring-brand-primary sm:text-sm p-2 border"
              value={currentAnnouncement.text}
              onChange={(e) => setCurrentAnnouncement({ ...currentAnnouncement, text: e.target.value })}
              placeholder="Geben Sie hier die Nachricht ein..."
            />
          </div>
          
          <div className="flex items-center justify-between pt-2">
            {!isEditing && (
                <p className="text-sm text-gray-500">
                   Hinweis: Ein neues Announcement wird automatisch als "Aktiv" gesetzt und deaktiviert alle anderen.
                </p>
            )}
            <div className="flex space-x-3 ml-auto">
              {isEditing && (
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setCurrentAnnouncement({ text: '', is_active: 1 });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
                >
                  Abbrechen
                </button>
              )}
              <button
                type="submit"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-brand-primary hover:bg-brand-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
              >
                {isEditing ? 'Speichern' : 'Erstellen'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md border border-gray-200">
        {isLoading ? (
          <div className="p-6 text-center text-gray-500">Lade Announcements...</div>
        ) : announcements.length === 0 ? (
          <div className="p-6 text-center text-gray-500">Keine Announcements vorhanden.</div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {announcements.map((announcement) => (
              <li key={announcement.id} className={`px-6 py-4 hover:bg-gray-50 ${announcement.is_active === 1 ? 'bg-blue-50/50' : ''}`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1 pr-4">
                    <div className="flex items-center mb-1">
                        {announcement.is_active === 1 && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">
                                Aktiv
                            </span>
                        )}
                    </div>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{announcement.text}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {announcement.is_active !== 1 && (
                        <button
                            onClick={() => handleActivate(announcement)}
                            className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                            title="Aktiv schalten"
                        >
                            <SafeIcon icon={FiPower} className="h-5 w-5" />
                        </button>
                    )}
                    <button
                      onClick={() => handleEdit(announcement)}
                      className="p-2 text-gray-400 hover:text-brand-primary transition-colors"
                      title="Bearbeiten"
                    >
                      <SafeIcon icon={FiEdit2} className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(announcement.id)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      title="Löschen"
                    >
                      <SafeIcon icon={FiTrash2} className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ManageAnnouncements;